import { useState } from "react";
import { Input } from "../../../components/common/input";
import { PhoneInput } from "../../../components/forms/phone-input";
import { Text } from "../../../components/common/typography";
import { Button } from "../../../components/common/button";
import {
  AddressFields,
  EMPTY_ADDRESS_FIELDS,
  type AddressFieldsValue,
} from "../../../components/forms/address-fields";
import {
  useAddressMutations,
  type BuyerAddressType,
} from "../hooks/useMyAddresses";
import { useCountries } from "../../products/hooks/useCountries";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";
import type { BuyerAddressDto, CreateBuyerAddressDto } from "../../../api";

interface Props {
  type: BuyerAddressType;
  onCreated: (address: BuyerAddressDto) => void;
  onCancel?: () => void;
}

interface FormState {
  contactName: string;
  address: AddressFieldsValue;
  phone: string;
  notes: string;
}

const EMPTY: FormState = {
  contactName: "",
  address: EMPTY_ADDRESS_FIELDS,
  phone: "",
  notes: "",
};

interface Copy {
  contactLabel: string;
  contactPlaceholder: string;
  contactHint?: string;
  notesLabel: string;
  notesPlaceholder: string;
  saveLabel: string;
}

const COPY: Record<BuyerAddressType, Copy> = {
  delivery: {
    contactLabel: "Delivery Contact Name",
    contactPlaceholder: "Full name of the recipient",
    contactHint: "Make sure this person is authorized to receive the items",
    notesLabel: "Delivery notes (optional)",
    notesPlaceholder: "e.g. ring at gate",
    saveLabel: "Save delivery address",
  },
  invoice: {
    contactLabel: "Billing Contact Name",
    contactPlaceholder: "Full name on the invoice",
    notesLabel: "Billing notes (optional)",
    notesPlaceholder: "e.g. PO reference",
    saveLabel: "Save billing address",
  },
  other: {
    contactLabel: "Contact Name",
    contactPlaceholder: "Full name",
    notesLabel: "Notes (optional)",
    notesPlaceholder: "Anything we should know",
    saveLabel: "Save address",
  },
};

/**
 * Type-parameterized form for creating an entry in the buyer's address book.
 * Posts to `POST /me/addresses` with the given type, then hands the freshly
 * created `BuyerAddressDto` back to the caller (which typically auto-selects
 * it). Labels and copy adapt to the address type so the form reads naturally
 * in checkout (delivery), payment (invoice), or settings (other).
 */
export function AddressForm({ type, onCreated, onCancel }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const { create, isCreating } = useAddressMutations();
  const { data: countries } = useCountries();
  const copy = COPY[type];

  const isValid =
    form.contactName.trim().length > 0 &&
    form.address.street.trim().length > 0 &&
    form.address.city.trim().length > 0 &&
    form.address.countryId.length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    const a = form.address;
    const dto: CreateBuyerAddressDto = {
      name: form.contactName.trim(),
      street: a.street.trim() || undefined,
      street2: a.street2.trim() || undefined,
      city: a.city.trim() || undefined,
      zip: a.zip.trim() || undefined,
      phone: form.phone.trim() || undefined,
      notes: form.notes.trim() || undefined,
      countryId: a.countryId ? Number(a.countryId) : undefined,
      stateId: a.stateId ? Number(a.stateId) : undefined,
    };
    try {
      const created = await create({ ...dto, type });
      showSuccessToast("Address saved");
      setForm(EMPTY);
      onCreated(created);
    } catch (err: any) {
      showErrorToast(
        err?.response?.data?.message ??
          err?.message ??
          "Could not save the address.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-4 pt-4">
      <Input
        label={copy.contactLabel}
        placeholder={copy.contactPlaceholder}
        value={form.contactName}
        onChange={(e) => setForm({ ...form, contactName: e.target.value })}
        required
      />
      {copy.contactHint && (
        <span className="text-accentAmber">
          <Text variant="bodyXs" tone="inherit">
            {copy.contactHint}
          </Text>
        </span>
      )}

      <AddressFields
        value={form.address}
        onChange={(address) => setForm((prev) => ({ ...prev, address }))}
        required={{ street: true, city: true, countryId: true }}
      />

      <div className="grid grid-cols-2 gap-4">
        <PhoneInput
          label="Phone number"
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v ?? "" })}
        />
        <Input
          label={copy.notesLabel}
          placeholder={copy.notesPlaceholder}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      {import.meta.env.DEV && (
        <div className="flex items-center justify-start pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const qa = countries?.find((c) => /qatar/i.test(c.name));
              const fallback = countries?.[0];
              const country = qa ?? fallback;
              setForm({
                contactName: "Dev Tester",
                address: {
                  street: "Al Corniche Street 42",
                  street2: "Building 7, Floor 3",
                  city: "Doha",
                  zip: "12345",
                  countryId: country ? String(country.id) : "",
                  stateId: "",
                },
                phone: "+97433123456",
                notes: "Dev autofill",
              });
            }}
          >
            Dev: autofill
          </Button>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {!isValid && (
          <Text variant="bodyXs" tone="muted" className="mr-auto">
            Fill in all fields marked * to save.
          </Text>
        )}
        {onCancel && (
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={isCreating}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!isValid || isCreating}
        >
          {isCreating ? "Saving…" : copy.saveLabel}
        </Button>
      </div>
    </div>
  );
}
